// utilities for working with workflow JSON structure
// All algorithms must be cycle-safe and match the exact schema

export function getStageById(workflow, id) {
  return workflow.stages.find((s) => s.id === id) || null;
}

export function getOutgoingConnections(workflow, stageId) {
  return (workflow.connections || []).filter(
    (c) => c.sourceStageId === stageId,
  );
}

export function getIncomingConnections(workflow, stageId) {
  return (workflow.connections || []).filter(
    (c) => c.targetStageId === stageId,
  );
}

export function getStartStages(workflow) {
  const targets = new Set(
    (workflow.connections || []).map((c) => c.targetStageId),
  );
  return (workflow.stages || []).filter((s) => !targets.has(s.id));
}

export function isTerminalStage(workflow, stageId) {
  return getOutgoingConnections(workflow, stageId).length === 0;
}

// breadth-first traversal from all start stages, return ordered list of stages
// also compute which stage ids are part of a cycle (i.e. an edge pointing back to a previously seen stage)
// visited set avoids infinite loops due to cycles
export function computeDisplayOrder(workflow) {
  const starts = getStartStages(workflow);
  const order = [];
  const visited = new Set();
  const cycleStageIds = new Set();
  const queue = [...starts];
  while (queue.length) {
    const s = queue.shift();
    if (visited.has(s.id)) continue;
    visited.add(s.id);
    order.push(s);
    const outs = getOutgoingConnections(workflow, s.id)
      .map((c) => getStageById(workflow, c.targetStageId))
      .filter(Boolean);
    for (const next of outs) {
      if (visited.has(next.id)) {
        // Found a back-edge -> cycle
        cycleStageIds.add(next.id);
        cycleStageIds.add(s.id);
      } else {
        queue.push(next);
      }
    }
  }
  return { order, cycleStageIds };
}

// useful for validation
export function validateConnections(workflow) {
  const errors = [];
  const stageIds = new Set((workflow.stages || []).map((s) => s.id));
  (workflow.connections || []).forEach((c) => {
    if (!stageIds.has(c.sourceStageId)) {
      errors.push(
        `Connection ${c.id} references unknown sourceStageId ${c.sourceStageId}`,
      );
    }
    if (!stageIds.has(c.targetStageId)) {
      errors.push(
        `Connection ${c.id} references unknown targetStageId ${c.targetStageId}`,
      );
    }
  });
  return errors;
}
